import React from 'react';
import CrtTitle from "./crtTitle";
import './App.css';

// Left-hugging navigation for the policy sections
const PRIVACY_TOC_SECTIONS = [
  { id: "priv-overview", label: "OVERVIEW" },
  { id: "priv-collect", label: "DATA WE COLLECT" },
  { id: "priv-why", label: "WHY WE PROCESS" },
  { id: "priv-sharing", label: "SHARING & TRANSFERS" },
  { id: "priv-security", label: "SECURITY" },
  { id: "priv-retention", label: "RETENTION" },
  { id: "priv-rights", label: "YOUR RIGHTS" },
  { id: "priv-processor", label: "PROCESSOR ROLE" },
  { id: "priv-contact", label: "CONTACT" },
];

// Card styling matched to the introduction section's description card
const cardStyle = {
  marginBottom: "70px",
  background: "rgba(0, 0, 0, 0.75)",
  padding: "25px 30px",
  borderRadius: "10px",
  fontSize: "0.64em",
  lineHeight: "1.7em",
  color: "#CCCCCC"
};

const cardHeadingStyle = {
  fontWeight: "bold",
  color: "#FFFFFF",
  marginBottom: "16px"
};

const paraStyle = {
  marginBottom: "16px"
};

const listStyle = {
  marginBottom: "16px",
  paddingLeft: "20px",
  listStyleType: "square"
};

class Privacy extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeSection: PRIVACY_TOC_SECTIONS[0].id
    };

    this.handleScroll = this.handleScroll.bind(this);
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.handleScroll();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    // Highlight the last section whose top has passed 40% of the viewport
    const threshold = window.innerHeight * 0.4;
    let active = PRIVACY_TOC_SECTIONS[0].id;

    PRIVACY_TOC_SECTIONS.forEach(section => {
      const el = document.getElementById(section.id);
      if (el && el.getBoundingClientRect().top <= threshold) {
        active = section.id;
      }
    });

    if (active !== this.state.activeSection) {
      this.setState({ activeSection: active });
    }
  }

  scrollToSection(id) {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  render() {
    return (
      <div>
        <div className="advisory-background">
        </div>

        <div className="intro-shield">
        </div>

        { /* Left-hugging section navigation */ }
        <nav className="advisory-toc noselect" aria-label="Privacy policy sections">
          {PRIVACY_TOC_SECTIONS.map(section => (
            <button
              type="button"
              key={section.id}
              className={`advisory-toc-item ${this.state.activeSection === section.id ? "advisory-toc-active" : ""}`}
              onClick={() => this.scrollToSection(section.id)}
              aria-current={this.state.activeSection === section.id ? 'location' : undefined}
            >
              {section.label}
            </button>
          ))}
        </nav>

        <div className="content" style={{ marginTop: "50px", minHeight: "60vh", paddingBottom: "80px"}}>

          <CrtTitle text="PRIVACY" style={{ marginBottom: "50px" }} />

          <div className="page-body">
            <div id="priv-overview" className="intro-column advisory-section" style={cardStyle}>
              <h2 className="card-heading" style={cardHeadingStyle}>OVERVIEW</h2>
              <div style={paraStyle}>
                This policy explains how <strong style={{ color: "#FFFFFF" }}>Relentless Partners (Pty) Ltd</strong> (South
                Africa) and <strong style={{ color: "#FFFFFF" }}>Relentless Technology, Inc</strong> (United States) —
                together, Relentless — collect and process personal information.  It covers this website, our
                products (Avarice ERP, Anarchy IMS and Konjur3D) and our advisory and assurance engagements.
              </div>
              <div style={paraStyle}>
                It is written to satisfy the South African Protection of Personal Information Act (POPIA), the EU and
                UK General Data Protection Regulation (GDPR) and applicable United States federal and state privacy
                laws, including the California Consumer Privacy Act as amended (CCPA/CPRA).  Where these frameworks
                differ, we apply the stricter standard.
              </div>
              <div>
                <strong style={{ color: "#FFFFFF" }}>This website does not use cookies</strong>, trackers or analytics.
                We receive information from the website only if you choose to email us.
              </div>
            </div>

            <div id="priv-collect" className="intro-column advisory-section" style={cardStyle}>
              <h2 className="card-heading" style={cardHeadingStyle}>DATA WE COLLECT</h2>
              <ul style={listStyle}>
                <li><strong style={{ color: "#FFFFFF" }}>Information you provide</strong> — when registering on one of our
                  platforms: name, mobile phone number, email address and location; and bank account details where the
                  platform pays money out to you.</li>
                <li><strong style={{ color: "#FFFFFF" }}>Information from your use of the platforms</strong> — device
                  information, IP addresses and activity data needed to operate, secure and improve the services.</li>
                <li><strong style={{ color: "#FFFFFF" }}>Engagement data</strong> — information provided under contract in
                  advisory, audit and assurance work, handled under the engagement's confidentiality terms.</li>
              </ul>
              <div>
                We collect only what each purpose requires — the minimality principle of POPIA section 10 and the data
                minimisation principle of GDPR Article 5.
              </div>
            </div>

            <div id="priv-why" className="intro-column advisory-section" style={cardStyle}>
              <h2 className="card-heading" style={cardHeadingStyle}>WHY WE PROCESS</h2>
              <div style={paraStyle}>
                We process personal information only on a lawful basis:
              </div>
              <ul style={listStyle}>
                <li>To conclude and perform a contract with you — account management, verification, service delivery and
                  payment;</li>
                <li>To comply with a legal obligation — including financial, tax and anti-money-laundering law;</li>
                <li>With your consent, which you may withdraw at any time without affecting prior processing;</li>
                <li>For our legitimate interests where these are not overridden by your rights — principally fraud
                  detection and prevention, and the security of our systems.</li>
              </ul>
              <div>
                Declining to provide information may limit your ability to use a platform, and we will tell you when
                that is the case.
              </div>
            </div>

            <div id="priv-sharing" className="intro-column advisory-section" style={cardStyle}>
              <h2 className="card-heading" style={cardHeadingStyle}>SHARING AND INTERNATIONAL TRANSFERS</h2>
              <div style={paraStyle}>
                Personal information is shared on a strict need-to-know basis with:
              </div>
              <ul style={listStyle}>
                <li>Authorized payment service providers, such as banks and reward providers;</li>
                <li>Authorized agents, service providers and subcontractors bound by written contract;</li>
                <li>A competent public authority or regulator where the law requires it.</li>
              </ul>
              <div style={paraStyle}>
                <strong style={{ color: "#FFFFFF" }}>We do not sell personal information</strong>, and we do not share it
                for cross-context behavioural advertising, in the CCPA/CPRA sense or any other.
              </div>
              <div>
                Where information moves between South Africa, the EU/UK and the United States, the transfer is protected
                by written contractual safeguards consistent with POPIA section 72 and Chapter V of the GDPR (including
                standard contractual clauses where applicable), so your information carries its protection with it.
              </div>
            </div>

            <div id="priv-security" className="intro-column advisory-section" style={cardStyle}>
              <h2 className="card-heading" style={cardHeadingStyle}>SECURITY</h2>
              <div style={paraStyle}>
                We apply layered technical and organizational measures: encryption in transit and for sensitive data at
                rest, authentication and access control, segregation of client environments, and regular testing and
                review of our controls.  Appropriate backup and disaster recovery arrangements are in place.
              </div>
              <div>
                If a security compromise affects your personal information, we notify the competent authority and
                affected individuals as required — under POPIA section 22, under GDPR Articles 33 and 34 (within 72
                hours to the supervisory authority), and under applicable US state breach notification laws.
              </div>
            </div>

            <div id="priv-retention" className="intro-column advisory-section" style={cardStyle}>
              <h2 className="card-heading" style={cardHeadingStyle}>RETENTION</h2>
              <ul style={listStyle}>
                <li>Platform account data is deleted when you ask us to close your participation, or after 3 years of
                  inactivity following our last interaction with you;</li>
                <li>Records of financial transactions are retained for the period financial, tax and anti-money-laundering
                  law requires in the relevant jurisdiction, typically 5 to 10 years;</li>
                <li>Information is kept longer only where a legal or regulatory reason requires it, and is deleted once
                  that reason falls away;</li>
                <li>Information is kept shorter where you object to processing and no legitimate ground to retain it
                  remains.</li>
              </ul>
            </div>

            <div id="priv-rights" className="intro-column advisory-section" style={cardStyle}>
              <h2 className="card-heading" style={cardHeadingStyle}>YOUR RIGHTS</h2>
              <div style={paraStyle}>
                Wherever you are, you may ask us what personal information we hold about you, ask us to correct or
                delete it, object to processing, and withdraw any consent you have given.  In addition:
              </div>
              <ul style={listStyle}>
                <li><strong style={{ color: "#FFFFFF" }}>South Africa (POPIA)</strong> — you hold the rights in sections
                  23 to 25, and may lodge a complaint with the Information Regulator of South Africa;</li>
                <li><strong style={{ color: "#FFFFFF" }}>EU, EEA and UK (GDPR)</strong> — you hold the rights in Articles
                  15 to 22, including data portability and restriction of processing, and may lodge a complaint with
                  your supervisory authority;</li>
                <li><strong style={{ color: "#FFFFFF" }}>United States</strong> — where state privacy law applies to you,
                  you may exercise your rights to know, correct, delete and opt out of sale or sharing (we do not sell
                  or share), and we will never discriminate against you for exercising them.</li>
              </ul>
              <div>
                To exercise any right, email us at the address below.  We verify identity before acting on a request and
                respond within the statutory period — one month under POPIA and the GDPR, 45 days under the CCPA/CPRA.
              </div>
            </div>

            <div id="priv-processor" className="intro-column advisory-section" style={cardStyle}>
              <h2 className="card-heading" style={cardHeadingStyle}>WHEN OUR CLIENTS RUN OUR SYSTEMS</h2>
              <div style={paraStyle}>
                When an organization deploys Avarice, Anarchy IMS or our assurance tooling for its own staff,
                customers or operations, that organization is the responsible party (controller) and Relentless acts as
                its operator (processor): we process personal information only under written contract and on the
                client's documented instructions.
              </div>
              <div>
                This applies in particular to fleet assurance deployments that involve workforce monitoring, which we
                undertake only within the client's disclosed policy and lawful mandate.  If your data reaches us this
                way, please direct requests to your organization first — we support our clients in fulfilling them.
              </div>
            </div>

            <div id="priv-contact" className="intro-column advisory-section" style={cardStyle}>
              <h2 className="card-heading" style={cardHeadingStyle}>CONTACT AND CHANGES</h2>
              <div style={paraStyle}>
                Privacy enquiries and requests, for all jurisdictions, go to our Information Officer
                at <a href="mailto:jon@relentless.co.za"><strong style={{ color: "#FFFFFF" }}>jon@relentless.co.za</strong></a>.
              </div>
              <div style={paraStyle}>
                This policy may change over time.  If a change is significant we will give appropriate notice, including
                by email where necessary.
              </div>
              <div>
                This version is effective from 14 August 2026.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Privacy;
